import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { AuthState } from '../types';
import { apiService } from '../services/api';
import GoogleSignInButton from '../components/ui/GoogleSignInButton';
import SEOHead from '../components/SEOHead';

interface LoginProps {
  setAuthState: (authState: AuthState) => void;
}

const Login: React.FC<LoginProps> = ({ setAuthState }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSuccess = async (credential: string) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.auth.googleLogin(credential);

      if (response.success && response.data) {
        // Store auth token in localStorage (match the key App.tsx expects)
        localStorage.setItem('authToken', response.data.token);

        // Update auth state
        setAuthState({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
        });


        // Check for redirect parameter
        const redirectTo = searchParams.get('redirect');

        // Validate redirect parameter to prevent open redirect attacks
        const allowedPaths = ['/dashboard', '/holdings', '/history', '/settings'];
        const isValidRedirect = redirectTo && allowedPaths.some(path => redirectTo.startsWith(path));

        if (isValidRedirect) {
          navigate(redirectTo);
        } else {
          // Default redirect to dashboard
          navigate('/dashboard');
        }
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error: Error) => {
    console.error('Google OAuth error:', error);
    setError(`Google sign-in error: ${error.message}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEOHead
        title="Login"
        description="Sign in to MarketEye to manage your portfolio and track your investments."
        canonicalUrl="/login"
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900">
              MarketEye
            </h1>
            <p className="mt-2 text-lg text-gray-600">Sign in to your account</p>
          </div>
        </Link>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Information Section */}
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure Sign In</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                No passwords to remember. Sign in securely with your Google account and start managing your portfolio.
              </p>
            </div>
          </div>

          {/* Google Sign-In Section */}
          <div className="space-y-4">
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={isLoading}
              text="signin_with"
              theme="outline"
              size="large"
            />

            {isLoading && (
              <div className="text-center">
                <div className="inline-flex items-center text-sm text-gray-500">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing you in...
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs text-center text-gray-500">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-700 underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 